import { EstimateTableFilter } from "@/types/estimate";
import { CombinedRow, ItemSheetObject } from "@/types/itemsheet";
import { create } from "zustand";
import { useMemo } from "react";
import { parseItemSheetObjectToRows } from "../utils";
import { ItemSheetExtraTag } from "@/consts/Estimate";

interface TableDataState {
  rows: CombinedRow[];
  filters: EstimateTableFilter;
  isEditing: boolean;
  isExcelDownloading: boolean;
  expandedRowIds: { [key: string]: boolean };
  undoStack: { rows: CombinedRow[] }[];
  redoStack: { rows: CombinedRow[] }[];
  backupRows: CombinedRow[];
  // 산출내역서 오리지널 데이터 추가 (페이지 이동에도 유지)
  estimateOriginalRows: CombinedRow[];
  hasEstimateOriginalData: boolean;

  setRows: (itemSheet: ItemSheetObject) => void;
  setFilter: (key: keyof EstimateTableFilter, value: string | string[]) => void;
  setIsEditing: (isEditing: boolean) => void;
  setIsExcelDownloading: (isExcelDownloading: boolean) => void;
  setExpandedRowId: (rowId: string, isExpanded: boolean) => void;
  setEstimateOriginalRows: (rows: CombinedRow[]) => void;
  undo: () => void;
  redo: () => void;
}

export const useTableStore = create<TableDataState>((set, get) => ({
  rows: [],
  filters: {
    ownProduct: "",
    manufacturer: [],
    vendor: [],
    buyer: [],
    targetPrice: "",
  },
  isEditing: false,
  isExcelDownloading: false,
  expandedRowIds: {},
  undoStack: [],
  redoStack: [],
  backupRows: [],
  estimateOriginalRows: [],
  hasEstimateOriginalData: false,

  setRows: (itemSheet) =>
    set((state) => {
      const parsedRows = parseItemSheetObjectToRows(itemSheet);

      return {
        rows: parsedRows,
        undoStack: [...state.undoStack, { rows: state.rows }],
        redoStack: [],
      };
    }),

  setFilter: (key, value) =>
    set((state) => {
      if (key === "manufacturer" || key === "vendor" || key === "buyer") {
        return {
          filters: { ...state.filters, [key]: value as string[] },
        };
      } else {
        return {
          filters: {
            ...state.filters,
            [key]: value === "all" ? "" : (value as string),
          },
        };
      }
    }),

  setIsEditing: (isEditing: boolean) => {
    if (isEditing) {
      set((state) => ({ backupRows: state.rows }));
    } else {
      set((state) => ({ rows: state.backupRows, backupRows: [] }));
    }
    set({
      isEditing,
      expandedRowIds: {},
    });
  },

  setIsExcelDownloading: (isExcelDownloading: boolean) =>
    set({ isExcelDownloading }),

  setExpandedRowId: (rowId, isExpanded) =>
    set((state) => ({
      expandedRowIds: {
        ...state.expandedRowIds,
        [rowId]: isExpanded,
      },
    })),

  setEstimateOriginalRows: (rows: CombinedRow[]) =>
    set({ estimateOriginalRows: rows, hasEstimateOriginalData: true }),

  undo: () => {
    const { undoStack, redoStack, rows } = get();
    if (undoStack.length === 0) return;

    const lastState = undoStack[undoStack.length - 1];
    set({
      rows: lastState.rows,
      undoStack: undoStack.slice(0, -1),
      redoStack: [...redoStack, { rows }],
    });
  },

  redo: () => {
    const { redoStack, undoStack, rows } = get();
    if (redoStack.length === 0) return;

    const nextState = redoStack[redoStack.length - 1];
    set({
      rows: nextState.rows,
      undoStack: [...undoStack, { rows }],
      redoStack: redoStack.slice(0, -1),
    });
  },
}));

export const useAllRows = () => {
  const { rows } = useTableStore();
  return useMemo(() => rows, [rows]);
};

export const useFilteredRows = () => {
  const { rows, filters } = useTableStore();

  return useMemo(() => {
    return rows.filter((row) => {
      return Object.entries(filters).every(([key, value]) => {
        if (key === "manufacturer" || key === "vendor" || key === "buyer") {
          const filterArray = value as string[];
          if (filterArray.length === 0) return true;

          const rowValue = row[key as keyof CombinedRow] as string;

          // __empty__ 선택이 포함된 경우 빈값도 허용
          if (
            filterArray.includes("__empty__") &&
            (!rowValue || rowValue === "")
          ) {
            return true;
          }

          // 선택된 값들 중 하나와 일치하는지 확인
          return filterArray.some((filterValue) => {
            if (filterValue === "__empty__") return false; // 이미 위에서 처리됨
            return rowValue === filterValue;
          });
        }

        if (!value || value === "all") return true;

        switch (key) {
          case "ownProduct":
            if (value === "true") {
              return row.tags.includes(ItemSheetExtraTag.OWN_PRODUCT);
            }
            if (value === "false") {
              return !row.tags.includes(ItemSheetExtraTag.OWN_PRODUCT);
            }
            return true;

          case "targetPrice":
            if (value === ItemSheetExtraTag.CUSTOM_PRICE) {
              return row.tags.includes(ItemSheetExtraTag.CUSTOM_PRICE);
            }
            if (value === ItemSheetExtraTag.FLOOR_PRICE) {
              return row.tags.includes(ItemSheetExtraTag.FLOOR_PRICE);
            }
            return true;

          default:
            return true;
        }
      });
    });
  }, [rows, filters]);
};

export const useIsEditing = () => useTableStore((state) => state.isEditing);
export const useExpandedRowIds = () =>
  useTableStore((state) => state.expandedRowIds);
export const useIsExcelDownloading = () =>
  useTableStore((state) => state.isExcelDownloading);
export const useFilters = () => useTableStore((state) => state.filters);

export const useSetIsEditing = () =>
  useTableStore((state) => state.setIsEditing);
export const useSetExpandedRowId = () =>
  useTableStore((state) => state.setExpandedRowId);
export const useSetIsExcelDownloading = () =>
  useTableStore((state) => state.setIsExcelDownloading);
export const useSetFilter = () => useTableStore((state) => state.setFilter);
export const useEstimateOriginalRows = () =>
  useTableStore((state) => state.estimateOriginalRows);
export const useHasEstimateOriginalData = () =>
  useTableStore((state) => state.hasEstimateOriginalData);
export const useSetEstimateOriginalRows = () =>
  useTableStore((state) => state.setEstimateOriginalRows);
